import Button from '@/components/Button';
import { assetUrl } from '@/utils/asset-url';
import CardConfigure from "@/componentsDS/Modal/CardConfigure";
import Modal from "@/componentsDS/Modal/Modal";
import googleKeys from '@/constants/configuration/google-keys.json'
import useModal from '@/hooks/use-modal';
import Container from '@/layouts/index';
import { Navigate, Route, SwitchRoutes, useLocation, useNavigate } from '@/lib/router-dom';
import { authGuard } from '@/middlewares/auth';
import { isAdmin } from '@/middlewares/is-admin';
import { isNotOperationUser } from '@/middlewares/is-non-ops-user';
import { isNonOrganisationParticipant } from '@/middlewares/is-non-organisation-participant/index';
import { isParticipant } from '@/middlewares/is-participant';
import { isSuperAdmin } from '@/middlewares/is-super-admin';
import { redirectGuard } from '@/middlewares/redirect';
import { tfaGuard } from '@/middlewares/tfa-guard';
import ApprovalRequestPage from '@/pages/approval-request/approval-request.jsx';
import EnrollmentRequest from '@/pages/approval-request/enrollment-request';
import PersonaApprovalRequestPage from '@/pages/approval-request/persona-approval-request.jsx';
import SGTDEnrollmentRequestPage from '@/pages/approval-request/sgtd-enrollment.jsx';
import AuditTrailPage from '@/pages/audit-trail.jsx';
import AuthRedirect from '@/pages/authRedirect.jsx';
import GlobalDashboardPage from '@/pages/dashboard.jsx';
import DataDictionaryPage from '@/pages/data-dictionary/index.jsx';
import { DSVListPage, DSVDetailPage, DSVUpdateSchemaPage, DSVComparePage } from '@/pages/data-schema-versioning/index.jsx';
import AddPitstopPage from '@/pages/data-element/pitstop/add.jsx';
import AddPitstopSuccessPage from '@/pages/data-element/pitstop/success.jsx';
import ForgetPasswordSuccessPage from '@/pages/forget-password-success.jsx';
import ForgetPasswordPage from '@/pages/forget-password.jsx';
import LoginPage from '@/pages/login.jsx';
import OnboardingClientPage from '@/pages/onboarding/client.jsx';
import OnboardingDataExchangePage from '@/pages/onboarding/data-exchange.jsx';
import NotificationPage from '@/pages/onboarding/notification-page.jsx';
import OnboardingServiceProviderPage from '@/pages/onboarding/service-provider.jsx';
import ParticipantDashboard from '@/pages/participant-dashboard.jsx';
import ParticipantHomePage from '@/pages/participant-home.jsx';
import Participants from '@/pages/participants.jsx';
import PitStopConfiguration from '@/pages/pitstop-configuration.jsx';
import PitStopLanding from '@/pages/pitstop-landing.jsx';
import RequestList from "@/pages/request-list.jsx";
import ResetPasswordSuccessPage from '@/pages/reset-password-success.jsx';
import ResetPasswordPage from '@/pages/reset-password.jsx';
import ResetTwoFactorAuthentication from '@/pages/reset-two-factor-authentication.jsx';
import ActivateTfa from '@/pages/setting-up-tfa.jsx';
import AccountSettingPage from '@/pages/setting/components/account-setting.jsx';
import SettingPage from '@/pages/setting/index.jsx';
import SignUpPage from '@/pages/signup/sign-up.jsx';
import PitStopSuccess from '@/pages/success.jsx';
import CreatePrincipal from '@/pages/use-cases/agent-principal/add-agent-principal.jsx';
import DashboardPage from '@/pages/use-cases/dashboard.jsx';
import ViewDataElementsList from '@/pages/use-cases/data-elements-list.jsx';
import EditPersonaPage from '@/pages/use-cases/edit-personas.jsx';
import PersonaPage from '@/pages/use-cases/enroll-personas.jsx';
import EnrolmentPage from "@/pages/use-cases/enrolment/index.jsx";
import EnrolmentSuccessPage from "@/pages/use-cases/enrolment/success.jsx";
import EditIssuancePage from '@/pages/use-cases/etr-issuance/edit-issuance.jsx';
import UseCasesPage from '@/pages/use-cases/index.jsx';
import Notification from '@/pages/use-cases/notifications.jsx';
import RequestSummary from '@/pages/use-cases/request/summary.jsx';
import AddEnrollPage from '@/pages/use-cases/sgtradex/enrol-sgtradex.jsx';
import ViewEnrolPage from '@/pages/use-cases/sgtradex/view-sgtradex.jsx';
import AddSubscriptionPage from '@/pages/use-cases/subscriptions/add.jsx';
import DataElementDetails from '@/pages/use-cases/subscriptions/data-element-details.jsx';
import EnrollSuccessPage from '@/pages/use-cases/success.jsx';
import SystemMonitoringPage from '@/pages/use-cases/system-monitoring.jsx';
import ViewInfoPage from '@/pages/use-cases/view-info.jsx';
import ViewPage from '@/pages/use-cases/view.jsx';
import UserManagement from '@/pages/user-management/user-management.jsx';
import { logout, renewToken } from '@/redux/actions/auth.js';
import { getEnvAndDex } from '@/utils/get-env-and-dex';
import rem from '@/utils/rem';
import googleTagManager from '@analytics/google-tag-manager';
import Analytics from 'analytics';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import ReactGA from 'react-ga';
import { useDispatch, useSelector } from 'react-redux';
import Portal from './components/Portal/index';
import Tooltip from './components/Tooltip/Tooltip';
import useFavicon from './hooks/use-favicon/index';
import { LayoutType } from './layoutDS/layoutDS';
import { useDexGuard } from './middlewares/dex-guard/dexGuard';
import EditPrincipalPage from './pages/use-cases/agent-principal/edit-principal';
import PrincipalSuccessPage from './pages/use-cases/agent-principal/success-page';
import EtrIssuancePage from './pages/use-cases/etr-issuance/setup-etr/etr-issuance-setup-persona';
import EtrIssuanceSuccessPage from './pages/use-cases/etr-issuance/success';
import useTheme from './hooks/use-theme/index';
import useAssetCssVars from './hooks/use-asset-css-vars/index';
import ModalTermEdit from './pages/setting/model-term-edit';
import { useFeature } from './hooks/use-feature/index';
import { featureConstant } from './constants/configuration/feature';

const { env, dex } = getEnvAndDex();

// Unified-portal (shell-hosted) builds only. The merged Account Settings route
// below is additive: legacy builds must not even register it.
const SHELL_HOSTED = process.env.REACT_APP_SHELL_HOSTED === 'true';

let analytics = null;

if (googleKeys?.[dex]?.[env]?.GOOGLE_GTM_ID) {
	analytics = Analytics({
		app: 'Admin Portal',
		plugins: [
			googleTagManager({
				containerId: `${googleKeys[dex]?.[env]?.GOOGLE_GTM_ID}`
			})
		]
	})
}

let popupTimer = null;
let logoutTimer = null;
let renewTokenTimer = null;

function App() {
	const dispatch = useDispatch()
	const { goTo } = useNavigate()
	const isLoggedIn = useSelector(state => state.auth.loggedIn)

	useFavicon();

	const [loading, setLoading] = useState(false);

	function onLoadingScreenClicked(event) {
		event.stopPropagation();
	}

	const { visible: visiblePopup, show: showPopup, hide: hidePopup } = useModal()
	const isIGPNILegalRequirementsEnabled = useFeature(featureConstant.IG_PNI_LEGAL_REQUIREMENTS)
	const isDSVEnabled = useFeature(featureConstant.DSV_ENABLED)

	const [logoutTime, setLogoutTime] = useState("")

	if (googleKeys?.[dex]?.[env]?.GOOGLE_GTM_ID) {
		analytics.page();
	}

	useTheme();
	useAssetCssVars();

	useEffect(() => {
		if (googleKeys?.[dex]?.[env]?.GOOGLE_TRACKING_ID) {
			ReactGA.initialize(`${googleKeys?.[dex]?.[env]?.GOOGLE_TRACKING_ID}`);
		}

		if (isLoggedIn) {
			window.onclick = addTimer
			window.onkeydown = addTimer
			addTimer()

		const nextRenew = Number(sessionStorage.getItem(RENEW_TOKEN_KEY));
		const now = Date.now();
		if (nextRenew && nextRenew > now) {
			renewTokenTimer = setTimeout(() => {
				dispatch(renewToken());
				setRenewTokenTimer(dispatch);
			}, nextRenew - now);
		} else {
			setRenewTokenTimer(dispatch);
		}

		}
		if (!isLoggedIn) {
			hidePopup()
			clearTimeout(popupTimer)
			clearTimeout(logoutTimer)
			clearTimeout(renewTokenTimer)
		}

		 return () => {
				clearTimeout(renewTokenTimer);
			};
	}, [isLoggedIn])

	function addTimer() {
		clearTimeout(popupTimer)
		clearTimeout(logoutTimer)

		setLogoutTime(moment().add(30, 'minutes').format('hh:mm A'))

		popupTimer = setTimeout(() => {
			showPopup()
		}, 1_500_000)

		logoutTimer = setTimeout(() => {
			hidePopup()
			dispatch(logout(goTo));
		}, 1_800_000)

	}

	const RENEW_TOKEN_KEY = 'nextRenewTokenTime';

    function setRenewTokenTimer(dispatch) {
    const now = Date.now();
	const nextRenew = now + 1_620_000;
    sessionStorage.setItem(RENEW_TOKEN_KEY, nextRenew);
    renewTokenTimer = setTimeout(() => {
        dispatch(renewToken());
        setRenewTokenTimer(dispatch);
    }, nextRenew - now);
}

	return (
		<>
			<div className="App" id={dex}>
				<SwitchRoutes>
					<Route path='/' element={<GuardedRoute component={GlobalDashboardPage} meta={{ auth: true, dispatch }} guards={[authGuard, redirectGuard]} />} />
					<Route path='/forget-password' element={<GuardedRoute component={ForgetPasswordPage} layout={LayoutType.Login} meta={{ auth: true, dispatch }} guards={[authGuard, redirectGuard]} />} />
					<Route path='/forget-password/success' element={<GuardedRoute component={ForgetPasswordSuccessPage} layout={LayoutType.Login} meta={{ auth: true, dispatch }} guards={[authGuard, redirectGuard]} />} />
					<Route path='/login' element={<GuardedRoute component={LoginPage} layout={LayoutType.Login} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, redirectGuard]} />} />
					<Route path='/reset-password' element={<GuardedRoute component={ResetPasswordPage} layout={LayoutType.Login} meta={{ auth: true, dispatch }} guards={[authGuard, redirectGuard]} />} />
					<Route path='/reset-password/success' element={<GuardedRoute component={ResetPasswordSuccessPage} layout={LayoutType.Login} meta={{ auth: true, dispatch }} guards={[authGuard, redirectGuard]} />} />
					<Route path='/reset-2fa' element={<GuardedRoute component={ResetTwoFactorAuthentication} layout={LayoutType.Login} meta={{ auth: true, dispatch }} guards={[authGuard]} />} />
					<Route path='/sign-up' element={<GuardedRoute component={SignUpPage} layout={LayoutType.Login} meta={{ auth: true, dispatch }} guards={[authGuard, redirectGuard]} />} />
					<Route path='/setup-tfa' element={<GuardedRoute component={ActivateTfa} layout={LayoutType.Login} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, redirectGuard]} />} />
					<Route path='/auth/redirect' element={<GuardedRoute component={AuthRedirect} layout={LayoutType.Login} meta={{ auth: true, dispatch }} />} />

					<Route path='/home' element={<GuardedRoute component={ParticipantHomePage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isNonOrganisationParticipant]} />} />

					<Route path='/data-dictionary' element={<GuardedRoute component={DataDictionaryPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
					{isDSVEnabled && (
						<>
							<Route path='/dsv' element={<GuardedRoute component={DSVListPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
							<Route path='/dsv/:dataElementId' element={<GuardedRoute component={DSVDetailPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
							<Route path='/dsv/:dataElementId/update-schema' element={<GuardedRoute component={DSVUpdateSchemaPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
							<Route path='/dsv/:dataElementId/compare' element={<GuardedRoute component={DSVComparePage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
						</>
					)}
					<Route path='/my-activities' element={<GuardedRoute component={AuditTrailPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />

					<Route path='/user-management' element={<GuardedRoute component={UserManagement} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isSuperAdmin]} />} />

					<Route path='/setting' element={<GuardedRoute component={SettingPage} setLoading={setLoading} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
					<Route path='/setting/account' element={<GuardedRoute component={SettingPage} setLoading={setLoading} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
					<Route path='/setting/notification' element={<GuardedRoute component={SettingPage} setLoading={setLoading} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
					{SHELL_HOSTED &&
						<Route path='/setting/pitstop-notification' element={<GuardedRoute component={SettingPage} setLoading={setLoading} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
					}
					{isIGPNILegalRequirementsEnabled && 
						<Route path='/setting/edit-clause/:clauseId' element={<GuardedRoute component={ModalTermEdit} setLoading={setLoading} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />}/>
					}

					<Route path='/admin/approval-requests' element={<GuardedRoute component={ApprovalRequestPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isAdmin]} />} />
					{/* <Route path='/admin/approval-requests/:requestType(add-persona|remove-persona)/:requestId' element={<GuardedRoute component={PersonaApprovalRequestPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isAdmin]} />} /> */}
					<Route path='/admin/approval-requests/:requestType/:requestId' element={<GuardedRoute component={PersonaApprovalRequestPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isAdmin]} />} />
					<Route path='/admin/approval-requests/:requestType/:requestId/:useCaseId' element={<GuardedRoute component={EnrollmentRequest} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isAdmin]} />} />
					<Route path='/admin/approval-requests/sgtd-enrollment/:requestId/:useCaseId/:orgId' element={<GuardedRoute component={SGTDEnrollmentRequestPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isAdmin]} />} />
					<Route path='/admin/participants' element={<GuardedRoute component={Participants} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isAdmin]} />} />
					<Route path='/admin/participants/:organizationId/user-management' element={<GuardedRoute component={UserManagement} additionalProps={{ role: 'admin' }} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isAdmin]} />} />
					<Route path='/admin/participants/dashboard' element={<GuardedRoute component={ParticipantDashboard} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isAdmin]} />} />
					<Route path='/admin/use-cases/:organizationId/personas' element={<GuardedRoute component={PersonaPage} additionalProps={{ role: "admin" }} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isAdmin]} />} />
					<Route path='/admin/use-cases/:organizationId/:useCaseId' element={<GuardedRoute component={ViewInfoPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
					<Route path='/admin/use-cases/:organizationId/:useCaseId/edit-personas' element={<GuardedRoute component={EditPersonaPage} additionalProps={{ role: "admin" }} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isAdmin]} />} />
					<Route path='/admin/use-cases/:organizationId/personas/success' element={<GuardedRoute component={EnrollSuccessPage} additionalProps={{ role: "admin" }} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isAdmin]} />} />

					<Route path='/dashboard' element={<GuardedRoute component={GlobalDashboardPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard,useDexGuard,  tfaGuard, isParticipant]} />} />
					{/* <Route path='/dashboard/:requestingType(review|request)/view' element={<GuardedRoute component={ViewPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} /> */}
					<Route path='/dashboard/:requestingType/view' element={<GuardedRoute component={ViewPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/dashboard/requests-list' element={<GuardedRoute component={RequestList} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/dashboard/view' element={<GuardedRoute component={ViewPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />

					<Route path='/pitstop' element={<GuardedRoute component={PitStopLanding} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/pitstop/configuration' element={<GuardedRoute component={PitStopConfiguration} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/pitstop/configuration/success' element={<GuardedRoute component={PitStopSuccess} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					{/* Onboarding V2 - Temporary placeholder*/}
					<Route path='/onboarding/my-data-exchange' element={<GuardedRoute component={OnboardingDataExchangePage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/onboarding/my-service-provider' element={<GuardedRoute component={OnboardingServiceProviderPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/onboarding/my-client' element={<GuardedRoute component={OnboardingClientPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/notification-page' element={<GuardedRoute component={NotificationPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />

					<Route path='/use-cases' element={<GuardedRoute component={UseCasesPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
					<Route path='/use-cases/enrolment/*' element={<GuardedRoute component={UseCasesPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
					<Route path='/use-cases/principal/*' element={<GuardedRoute component={UseCasesPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
					<Route path='/use-cases/:useCaseId' element={<GuardedRoute component={ViewInfoPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />

					{/* <Route path='/use-cases/:useCaseId/:dataElementId/add-:subscriptionType(consumer|provider)' element={<GuardedRoute component={AddSubscriptionPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} /> */}
					<Route path='/use-cases/:useCaseId/:dataElementId/:subscriptionType' element={<GuardedRoute component={AddSubscriptionPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/:useCaseId/:dataElementId/:subscriptionType/participant/:participantId/edit-principal' element={<GuardedRoute component={EditPrincipalPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					{/* <Route path='/use-cases/:useCaseId/:requestingType(review|request)/view' element={<GuardedRoute component={ViewPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} /> */}
					<Route path='/use-cases/:useCaseId/:requestingType/view' element={<GuardedRoute component={ViewPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
					<Route path='/use-cases/:useCaseId/dashboard' element={<GuardedRoute component={DashboardPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant, isNotOperationUser]} />} />
					<Route path='/use-cases/:useCaseId/data-elements-list' element={<GuardedRoute component={ViewDataElementsList} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					{/* <Route path='/use-cases/:useCaseId/data-elements/:dataElementId/:enrolmentType(provider|consumer|provide_on_behalf|consume_on_behalf)' element={<GuardedRoute component={DataElementDetails} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} /> */}
					<Route path='/use-cases/:useCaseId/data-elements/:dataElementId/:enrolmentType/system/:systemId/edit-principal' element={<GuardedRoute component={EditPrincipalPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
					<Route path='/use-cases/:useCaseId/data-elements/:dataElementId/:enrolmentType' element={<GuardedRoute component={DataElementDetails} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/:useCaseId/edit-personas' element={<GuardedRoute component={EditPersonaPage} additionalProps={{ role: "participant" }} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/:useCaseId/edit-issuance' element={<GuardedRoute component={EditIssuancePage} additionalProps={{ role: "participant" }} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/:useCaseId/enroll/success' element={<GuardedRoute component={EnrollSuccessPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isNonOrganisationParticipant]} />} />
					{/* <Route path='/use-cases/:useCaseId/enrolment/:enrolmentType(provider|consumer|provide_on_behalf|consume_on_behalf)' element={<GuardedRoute component={EnrolmentPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} /> */}
					<Route path='/use-cases/:useCaseId/enrolment/:enrolmentType' element={<GuardedRoute component={EnrolmentPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/:useCaseId/enrolment/success' element={<GuardedRoute component={EnrolmentSuccessPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/:useCaseId/personas' element={<GuardedRoute component={PersonaPage} additionalProps={{ role: "participant" }} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/:useCaseId/personas/success' element={<GuardedRoute component={EnrollSuccessPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					{/* <Route path='/use-cases/:useCaseId/pitstop/:dataElementId/:enrolmentType(provider|consumer|provide_on_behalf|consume_on_behalf)' element={<GuardedRoute component={AddPitstopPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} /> */}
					<Route path='/use-cases/:useCaseId/pitstop/:dataElementId/:enrolmentType' element={<GuardedRoute component={AddPitstopPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/:useCaseId/pitstop/success' element={<GuardedRoute component={AddPitstopSuccessPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/:useCaseId/principal/success' element={<GuardedRoute component={PrincipalSuccessPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/:useCaseId/requests-list' element={<GuardedRoute component={RequestList} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/:useCaseId/requests/summary' element={<GuardedRoute component={RequestSummary} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/:useCaseId/system-monitoring' element={<GuardedRoute component={SystemMonitoringPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/enrol/sgtradex' element={<GuardedRoute component={AddEnrollPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isNonOrganisationParticipant]} />} />
					<Route path='/use-cases/view/sgtradex' element={<GuardedRoute component={ViewEnrolPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isNonOrganisationParticipant]} />} />
					<Route path='/use-cases/principal/create' element={<GuardedRoute component={CreatePrincipal} additionalProps={{ input: "manual" }} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isSuperAdmin]} />} />
					<Route path='/use-cases/principal/create-bulk' element={<GuardedRoute component={CreatePrincipal} additionalProps={{ input: "bulk" }} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isSuperAdmin]} />} />
					<Route path='/use-cases/etr-issuance/:useCaseId/personas' element={<GuardedRoute component={EtrIssuancePage} additionalProps={{ role: "participant" }} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/etr-issuance/:useCaseId/personas/success' element={<GuardedRoute component={EtrIssuanceSuccessPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />

					{/* Legacy pages not in use */}
					<Route path='/notification' element={<GuardedRoute component={Notification} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
					<Route path='/use-cases/:useCaseId/view-detail/:requestId/:requestType' element={<GuardedRoute component={ViewPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard, isParticipant]} />} />
					<Route path='/use-cases/:useCaseId/view' element={<GuardedRoute component={ViewPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
					<Route path='/account-setting' element={<GuardedRoute component={AccountSettingPage} layout={LayoutType.Default} meta={{ auth: true, dispatch }} guards={[authGuard, tfaGuard]} />} />
				</SwitchRoutes>
			</div>

			<Modal visible={visiblePopup} variant='medium' close={hidePopup} dismissClickOutsideModal={false}>
				<CardConfigure >
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											<span className='inactive-title'>Auto Logout</span>
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							<div className="inactive-text">
								Your session has been idle for over 25 minutes. You will be logged out at <span className='inactive-time'>{logoutTime}</span>. Do you want to stay logged in your session?
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button ghost text="Logout"
								submit={() => {
									dispatch(logout(goTo));
								}}
							/>
							<span style={{ width: rem(5), display: 'inline-block' }}></span>
							<Button full maxWidth="140" text="Continue Session"
								submit={async () => { try {
									hidePopup(); 
									await dispatch(renewToken());
									addTimer();
								} catch (e) {
									console.error("Error renewing token:", e); }}}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			<Portal id="app-blocker">
				{loading && (
					<div className="modal white" onClick={onLoadingScreenClicked}>
						<div className="loading">
							<img src={assetUrl("/loading.gif")} />
						</div>
					</div>
				)}
			</Portal>

			<Tooltip />
		</>

	)
}

const useGuards = (guards, meta) => {
	const location = useLocation();

	const metaInformation = {
		...meta,
		location
	}

	if (!guards) return null;

	for (let guard of guards) {
		const redirectPath = guard(metaInformation);
		if (redirectPath) {
			return redirectPath;
		}
	}
	return null;
};

function GuardedRoute({ component: Component, layout, guards, meta, setLoading, additionalProps, ...props }) {
	const location = useLocation();
	const redirectPath = useGuards(guards, meta);

	if (redirectPath && location.currentPath !== redirectPath) {
		return <Navigate to={redirectPath} replace={true} />;
	}

	const RenderedComponent = (

		<Container layout={layout}>
			<Component {...props} {...additionalProps} setLoading={setLoading} />
		</Container>
	);

	return RenderedComponent;
}

GuardedRoute.propTypes = {
	component: PropTypes.elementType.isRequired,
	layout: PropTypes.string,
	guards: PropTypes.arrayOf(PropTypes.func),
	meta: PropTypes.object,
	setLoading: PropTypes.func,
	additionalProps: PropTypes.object,
};

export default App
